import React, { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { AccessDenied, canSeeMoney, isOrderInScope } from '../adminAccess';
import { calculateOrderMoney, isPaidOrder } from '../financeUtils';
import DatabaseError from '../components/DatabaseError';

function money(value) {
  return `${Number(value || 0).toFixed(2)} ₼`;
}

function customerName(order) {
  return (
    order.customerName ||
    order.customerPhone ||
    order.phone ||
    order.customerEmail ||
    order.customerId ||
    'Customer'
  );
}

function paymentMethod(order) {
  return (
    order.paymentMethod ||
    order.paymentGateway ||
    order.gateway ||
    order.paymentProvider ||
    '—'
  );
}

function paymentReference(order) {
  return (
    order.paymentGatewayRef ||
    order.paymentReference ||
    order.transactionId ||
    order.paymentId ||
    order.gatewayTransactionId ||
    '—'
  );
}

function getDateValue(value) {
  if (!value) return null;

  // Firestore Timestamp
  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  // Firestore Timestamp-like object
  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

function paidDate(order) {
  const value =
    order.paidAt ||
    order.paymentCompletedAt ||
    order.paymentPaidAt ||
    order.paymentUpdatedAt ||
    order.updatedAt ||
    order.createdAt;

  const date = getDateValue(value);

  if (!date) return '—';

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function successfulPaymentAmount(order) {
  const moneyInfo = calculateOrderMoney(order);

  // Reuse the project's existing finance calculation for the actual amount
  // received from the customer.
  return Number(moneyInfo.customerPaid || 0);
}

export default function FinancePage({ user }) {
  const [orders, setOrders] = useState([]);
  const [dbError, setDbError] = useState('');

  useEffect(() => {
    const unsubscribe = onSnapshot(
      query(collection(db, 'orders'), orderBy('createdAt', 'desc')),
      snapshot => {
        setDbError('');

        setOrders(
          snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
          }))
        );
      },
      error => {
        console.error(error);

        setDbError(
          error?.code === 'permission-denied'
            ? 'Permission denied. Please log out and log in again as the super admin.'
            : error?.message || 'Unable to read payment records.'
        );
      }
    );

    return () => unsubscribe();
  }, []);

  /*
   * First respect the current Admin scope/security rules.
   * Then show ONLY successful client payments.
   */
  const successfulPayments = useMemo(() => {
    return orders
      .filter(order => isOrderInScope(user, order))
      .filter(isPaidOrder);
  }, [orders, user]);

  const totalReceived = useMemo(() => {
    return successfulPayments.reduce(
      (total, order) => total + successfulPaymentAmount(order),
      0
    );
  }, [successfulPayments]);

  if (!canSeeMoney(user)) {
    return (
      <AccessDenied title="Finance is only for the super admin" />
    );
  }

  return (
    <div className="page-shell">
      {/* -------------------------------------------------------------
          HEADER
      ------------------------------------------------------------- */}
      <div className="mb-6">
        <h1 className="page-title">Finance & Payments</h1>

        <p className="text-sm text-gray-500 mt-1">
          View successful client payments received through PakMazar.
        </p>
      </div>

      <DatabaseError message={dbError} />

      {/* -------------------------------------------------------------
          SUMMARY
      ------------------------------------------------------------- */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card
          label="Successful Payments"
          value={successfulPayments.length}
          icon="✅"
          tone="green"
        />

        <Card
          label="Total Received"
          value={money(totalReceived)}
          icon="💳"
          tone="blue"
        />
      </div>

      {/* -------------------------------------------------------------
          INFO
      ------------------------------------------------------------- */}
      <div className="bg-green-50 border border-green-100 rounded-2xl px-5 py-4 mb-6">
        <div className="flex items-start gap-3">
          <div className="text-xl">💰</div>

          <div>
            <h2 className="font-bold text-green-900">
              Successful client payments
            </h2>

            <p className="text-sm text-green-800 mt-1">
              Only confirmed successful payments are included on this page and
              in the total received amount. Pending, unpaid, failed and
              cancelled payments are excluded.
            </p>
          </div>
        </div>
      </div>

      {/* -------------------------------------------------------------
          SUCCESSFUL PAYMENTS TABLE
      ------------------------------------------------------------- */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="font-bold text-gray-900">
            Payment History
          </h2>

          <p className="text-xs text-gray-500 mt-1">
            Confirmed payments received from PakMazar clients.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1050px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                {[
                  'Order',
                  'Client',
                  'Amount',
                  'Payment Method',
                  'Payment Reference',
                  'Paid At',
                  'Status',
                ].map(header => (
                  <th
                    key={header}
                    className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase whitespace-nowrap"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {successfulPayments.map(order => {
                const amount = successfulPaymentAmount(order);

                return (
                  <tr
                    key={order.id}
                    className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
                  >
                    {/* Order */}
                    <td className="px-4 py-4">
                      <span className="font-mono text-xs text-gray-600 whitespace-nowrap">
                        {order.orderNumber || order.id.slice(-8)}
                      </span>
                    </td>

                    {/* Client */}
                    <td className="px-4 py-4">
                      <div className="font-medium text-gray-900">
                        {customerName(order)}
                      </div>

                      {order.customerEmail && (
                        <div className="text-xs text-gray-400 mt-0.5">
                          {order.customerEmail}
                        </div>
                      )}
                    </td>

                    {/* Amount */}
                    <td className="px-4 py-4">
                      <span className="font-bold text-green-700 whitespace-nowrap">
                        {money(amount)}
                      </span>
                    </td>

                    {/* Method */}
                    <td className="px-4 py-4 text-gray-700 whitespace-nowrap">
                      {paymentMethod(order)}
                    </td>

                    {/* Reference */}
                    <td className="px-4 py-4">
                      <span className="font-mono text-xs text-gray-500">
                        {paymentReference(order)}
                      </span>
                    </td>

                    {/* Paid date */}
                    <td className="px-4 py-4 text-gray-600 whitespace-nowrap">
                      {paidDate(order)}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-4">
                      <span className="inline-flex items-center rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700 border border-green-100">
                        Paid
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {successfulPayments.length === 0 && (
          <div className="text-center py-16 px-4">
            <div className="text-4xl mb-3">💳</div>

            <p className="font-semibold text-gray-700">
              No successful payments yet
            </p>

            <p className="text-sm text-gray-400 mt-1">
              Confirmed client payments will appear here automatically.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ label, value, icon, tone = 'gray' }) {
  const colors = {
    gray: 'bg-gray-50 text-gray-700',
    green: 'bg-green-50 text-green-700',
    blue: 'bg-blue-50 text-blue-700',
  };

  return (
    <div className="stat-card">
      <div
        className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl mb-3 ${
          colors[tone] || colors.gray
        }`}
      >
        {icon}
      </div>

      <p className="text-2xl font-bold text-gray-900">
        {value}
      </p>

      <p className="text-xs text-gray-500 mt-0.5">
        {label}
      </p>
    </div>
  );
}